import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import clsx from "clsx";
import React from "react";

import { useWorkspaceConnections } from "@web-app/api";
import { Button, InfoBox, Modal, SelectInput } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  BaseApp,
  Order,
  Route,
  RouteStatus,
  User,
  WorkspaceConnection,
} from "@web-app/types";
import {
  getMetersToMiles,
  pluralize,
  timeZoneMap,
} from "@web-app/utils/helpers";
import {
  REPORT_PILL_COLOR,
  ROUTE_STATUS_BG_COLORS,
} from "@web-app/utils/order";

import Avatar from "../Avatar";
import CardPill from "../CardPill";
import DateTimeTz from "../DateTimeTz";
import ETAWithLabel from "../ETAWithLabel";
import HighlightString from "../HighlightString";
import { Icon } from "../Icon";
import OrderCard from "../OrderCard";
import { getExternalAssigneeTypeFromId } from "../OrderCard/utils";
import SendToMenu from "../SendToMenu";
import Skeleton from "../Skeleton";

import { CopyRouteModal } from "./DuplicateRouteModal";
import useRouteCard from "./useRouteCard";

export default function RouteCard({
  appsMap,
  assigneesMap,
  className,
  isAppsLoading,
  isAssigneesLoading,
  isAutoAssignSearching,
  route,
  routedOrdersMap,
  searchTerm,
  isDefaultExpanded,
  isPopover,
  onSelectExternalProvider,
}: {
  appsMap?: Map<string, BaseApp>;
  assigneesMap?: Map<string, User>;
  className?: string;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isAutoAssignSearching?: boolean;
  isDefaultExpanded?: boolean;
  isPopover?: boolean;
  onSelectExternalProvider?: (
    entity: Order | Route | null,
    provider: string,
    type: "order" | "route",
  ) => void;

  route: Route;
  routedOrdersMap: Map<string, Order>;
  searchTerm: string;
}) {
  const {
    copyRoute,
    routeStatus,
    routeScheduledStartDateTime,
    routeScheduledEndDateTime,
    assignee,
    isExpanded,
    setIsExpanded,
    handleClickCrosshair,
    handleCopyRoute,
    isCopyModalOpen,
    setIsCopyModalOpen,
    status,
    orders,
    routeTimezone,
    onClickEdit,
    getTotalOrderCount,
    onRouteOrdersCardViewDetails,
    mapFocusedRouteId,
    workspace,
    connectedWorkspace,
    workspaceConnections,
    isShareModalVisible,
    setIsShareModalVisible,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    onShare,
    hasReportsInRouteOrders,
  } = useRouteCard({
    assigneesMap,
    isDefaultExpanded,
    route,
    routedOrdersMap,
    searchTerm,
  });

  const openExternalProviderModal = (provider: string) => {
    if (!onSelectExternalProvider) return;
    onSelectExternalProvider(route, provider, "route");
  };
  const { user } = useDispatchStore();

  return (
    <div
      className={clsx(
        "flex py-[4px] lg:py-[8px] px-[6px] lg:px-[12px] flex-col items-start gap-[10px] rounded-lg border bg-gray-100",
        className,
        mapFocusedRouteId === route.id && "border-gray-400",
        isPopover ? "w-[450px]" : "w-full",
      )}
    >
      <div className="flex justify-between items-center self-stretch">
        <div className="flex items-center gap-[24px]">
          <div className="flex items-center gap-[4px] ">
            <Icon name="fasRoute" className="text-[#ADAFB3] w-3 lg:w-4" />
            {route.isFlexible && (
              <Icon name="fasShuffle" className="text-[#ADAFB3] w-3 lg:w-4" />
            )}
            <div
              className={clsx(
                "flex gap-[6px]  py-[1px] lg:py-[3.5px] justify-center items-center  font-normal font-inter text-[10px] lg:text-xs leading-none text-center rounded-[33px]",
              )}
            >
              {route.externalId && (
                <span className="truncate inline-block max-w-[30px] md:max-w-[100px] text-gray-600">
                  <HighlightString
                    text={route.externalId}
                    searchTerm={searchTerm}
                  />
                </span>
              )}
              <CardPill
                text={routeStatus}
                className={`py-0.5 lg:py-0 ${ROUTE_STATUS_BG_COLORS[routeStatus].card.darkBackground}`}
              />
              {hasReportsInRouteOrders && (
                <CardPill text="Reported" className={REPORT_PILL_COLOR} />
              )}
            </div>
            {(() => {
              const esaType =
                route.externalAssignee?.type ||
                getExternalAssigneeTypeFromId(route.externalAssigneeId);
              if (!esaType) return null;

              if (
                esaType === "expedite" &&
                route.externalAssignee?.remark !== "accepted"
              ) {
                return null;
              }
              return (
                <img
                  src={`/images/${esaType}.png`}
                  alt={esaType}
                  className="w-[16px]"
                />
              );
            })()}
          </div>
        </div>
        {route.estimatedDistance ? (
          <div className="text-[10px] lg:text-xs text-gray-600">
            {getMetersToMiles(route.estimatedDistance)} miles
          </div>
        ) : null}
        <div className="text-[10px] lg:text-xs text-gray-600">
          {getTotalOrderCount} {pluralize("Order", getTotalOrderCount)}
        </div>
        <div className="flex items-center gap-[6px] lg:gap-[10px] relative">
          <div
            className={clsx(
              "rounded-md flex justify-center items-center p-1 hover:bg-gray-200 text-gray-600",
              mapFocusedRouteId === route.id && "bg-gray-200",
            )}
            onClick={handleClickCrosshair}
          >
            <Icon name="locationCrossHairs" className="h-3 cursor-pointer" />
          </div>
          <Icon
            name={!isExpanded ? "fasChevronDown" : "fasChevronUp"}
            onClick={() => setIsExpanded(!isExpanded)}
            className=" w-[10px] lg:w-3 rounded h-[10px] lg:h-3 p-1 text-gray-500 bg-white cursor-pointer"
          />
          <Menu>
            {({ close }) => (
              <>
                <MenuButton
                  data-testid="cy-order-options"
                  className="flex items-center cursor-pointer"
                >
                  <Icon
                    name="fasEllipsis"
                    className="w-[10px] lg:w-3 h-[10px] lg:h-3 p-1 text-gray-500 cursor-pointer rounded-md ease-in leading-none bg-white"
                  />
                </MenuButton>
                <MenuItems
                  anchor="bottom end"
                  className="z-50 w-40 origin-top-right divide-y rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-out duration-100 data-[closed]:opacity-0 data-[closed]:scale-95"
                >
                  <MenuItemV2 onClick={onClickEdit}>See Details</MenuItemV2>
                  <MenuItemV2 onClick={() => setIsCopyModalOpen(true)}>
                    <Icon className="mr-2 h-3 w-3" name="copy" />
                    Copy
                  </MenuItemV2>
                  <SendToMenu
                    entity={{
                      externalAssigneeId: route.externalAssigneeId ?? "",
                      id: route.id,
                      numOfOrders: route.ordersOrder.length,
                      type: "ROUTE",
                    }}
                    close={close}
                    openExternalProviderModal={openExternalProviderModal}
                    setIsShareModalVisible={setIsShareModalVisible}
                    onShare={
                      user?.currentWorkspaceId === route.workspaceId
                        ? onShare
                        : undefined
                    }
                    workspace={workspace}
                    connectedWorkspace={connectedWorkspace}
                    filteredWorkspaceConnections={workspaceConnections || []}
                  />
                </MenuItems>
              </>
            )}
          </Menu>
        </div>
      </div>

      {isExpanded ? (
        <OrderContainer
          appsMap={appsMap}
          assigneesMap={assigneesMap}
          isAppsLoading={isAppsLoading}
          isAssigneesLoading={isAssigneesLoading}
          routeOrders={route.ordersOrder}
          onViewDetails={onRouteOrdersCardViewDetails}
          searchTerm={searchTerm}
          routeOrdersMap={routedOrdersMap}
          isPopover={isPopover}
          route={route}
        />
      ) : (
        <div className="flex justify-between items-center self-stretch mt-[7px] text-gray-600 text-xs">
          <div className="flex items-center gap-[8px]">
            {isAssigneesLoading && route.assigneeId ? (
              <>
                <Skeleton height="h-6" width="w-6" className="rounded-full" />
                <Skeleton height="h-3" width="w-16" className="rounded" />
              </>
            ) : assignee ? (
              <>
                <Avatar
                  image={assignee?.image || ""}
                  name={assignee?.name || ""}
                  size={6}
                />
                <span className="w-10 md:w-20 text-[10px] lg:text-xs truncate">
                  <HighlightString
                    text={assignee?.name || ""}
                    searchTerm={searchTerm}
                  />
                </span>
              </>
            ) : isAutoAssignSearching ? (
              <span className="flex items-center gap-2 text-blue-500">
                <Icon name="spinner" className="h-4 w-4 animate-spin" />
                <span className="text-[10px] lg:text-xs">Searching...</span>
              </span>
            ) : null}
          </div>
          <RouteETA
            routeStatus={status}
            orders={orders}
            route={route}
            timezone={routeTimezone}
          />
          {routeScheduledStartDateTime && routeScheduledEndDateTime && (
            <DateTimeTz
              start={new Date(routeScheduledStartDateTime)}
              end={new Date(routeScheduledEndDateTime)}
              timezone={routeTimezone ?? "system"}
              presentation="flat"
            />
          )}
        </div>
      )}

      <RouteSendModal
        workspaceConnections={workspaceConnections}
        handleShareOrder={() => {
          if (selectedWorkspaceId) {
            onShare(selectedWorkspaceId);
          }
        }}
        isShareModalVisible={isShareModalVisible}
        selectedWorkspaceId={selectedWorkspaceId}
        setIsShareModalVisible={setIsShareModalVisible}
        setSelectedWorkspaceId={setSelectedWorkspaceId}
      />

      <CopyRouteModal
        isLoading={copyRoute?.isPending ?? false}
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        onCopy={handleCopyRoute}
        route={route}
        timezone={routeTimezone}
      />
    </div>
  );
}

function RouteETA({
  routeStatus,
  timezone,
  orders,
  route,
}: {
  orders: (Order | undefined)[];
  route: Route;
  routeStatus: RouteStatus;
  timezone: keyof typeof timeZoneMap | undefined;
}) {
  if (routeStatus === "COMPLETE") {
    const completedAt = route.completedAt;
    if (!completedAt) return null;
    return (
      <ETAWithLabel
        label="Completed At"
        date={completedAt}
        timezone={timezone}
      />
    );
  }
  const lastOrderEta = orders[orders.length - 1]?.eta?.dateTime;
  if (lastOrderEta) {
    return (
      <ETAWithLabel
        label="ETA"
        date={new Date(lastOrderEta)}
        timezone={timezone}
      />
    );
  }
  return null;
}

function OrderContainer({
  appsMap,
  assigneesMap,
  isAppsLoading,
  isAssigneesLoading,
  routeOrders,
  routeOrdersMap,
  onViewDetails,
  searchTerm,
  isPopover,
  route,
}: {
  appsMap?: Map<string, BaseApp>;
  assigneesMap?: Map<string, User>;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isPopover?: boolean;
  onViewDetails: (orderId: string) => void;
  route: Route;
  routeOrders: Route["ordersOrder"];
  routeOrdersMap: Map<string, Order>;
  searchTerm: string;
}) {
  const { data: workspaceConnections } = useWorkspaceConnections();

  return (
    <>
      <div
        className={clsx(
          "w-full max-w-full flex gap-[5px] flex-col",
          isPopover && "max-h-[250px] overflow-y-auto",
        )}
      >
        {routeOrders.map((routeOrder, index) => {
          if (!routeOrdersMap) return null;

          const order = routeOrdersMap.get(routeOrder.id);

          if (!order) {
            return null;
          }

          const isOrderCompleted = order.status === "COMPLETE";
          const isOrderFailed =
            order.status === "FAILED" || order.status === "CANCELLED";
          const nextOrderStatus =
            index !== routeOrders.length - 1 &&
            routeOrdersMap.get(routeOrders[index + 1].id)?.status;
          const isNextOrderCompleted = nextOrderStatus === "COMPLETE";
          const isNextOrderFailed =
            nextOrderStatus === "FAILED" || nextOrderStatus === "CANCELLED";
          return (
            <div
              className={clsx(
                "flex flex-row w-full items-center gap-y-[4px] before:lg:h-2 before:lg:w-2 relative before:lg:absolute before:lg:left-0 before:lg:top-1/2 before:lg:rounded-full after:lg:w-[2px] after:lg:top-[calc(50%+4px)] before:lg:-translate-y-1/2 after:lg:-translate-y-1 after:lg:h-[calc(100%+2px)] after:lg:z-0 before:lg:z-10 after:lg:last:hidden after:lg:absolute after:lg:left-[3px] ",
                isOrderCompleted
                  ? "before:bg-green-500"
                  : isOrderFailed
                    ? "before:bg-red-500"
                    : "before:bg-gray-300",
                isNextOrderCompleted
                  ? "after:bg-green-500"
                  : isNextOrderFailed
                    ? "after:bg-red-500"
                    : "after:bg-gray-300",
              )}
              key={order.id}
            >
              <div className="lg:ml-4 w-full overflow-hidden">
                <OrderCard
                  className="!static"
                  order={order}
                  app={order.appId && appsMap ? appsMap.get(order.appId) : null}
                  isAppsLoading={isAppsLoading}
                  assignee={
                    order.assigneeId && assigneesMap
                      ? assigneesMap.get(order.assigneeId)
                      : null
                  }
                  isAssigneesLoading={isAssigneesLoading}
                  onViewDetails={() => onViewDetails(order.id)}
                  route={route}
                  routeIndex={index + 1}
                  searchTerm={searchTerm}
                  workspaceConnections={workspaceConnections}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function MenuItemV2({
  onClick,
  children,
}: {
  children: React.ReactNode;
  onClick: VoidFunction;
}) {
  return (
    <MenuItem>
      <span
        className="flex cursor-pointer items-center p-2 py-2 lg:py-1 text-xs lg:text-sm font-normal hover:bg-gray-200 text-gray-600"
        onClick={onClick}
      >
        <span className="leading-none text-xs lg:text-sm">{children}</span>
      </span>
    </MenuItem>
  );
}

function RouteSendModal({
  workspaceConnections,
  handleShareOrder,
  selectedWorkspaceId,
  setSelectedWorkspaceId,
  isShareModalVisible,
  setIsShareModalVisible,
}: {
  handleShareOrder: () => void;
  isShareModalVisible: boolean;
  selectedWorkspaceId: string;
  setIsShareModalVisible: (visible: boolean) => void;
  setSelectedWorkspaceId: (id: string) => void;
  workspaceConnections: WorkspaceConnection[] | undefined;
}) {
  return (
    <Modal
      isOpen={isShareModalVisible}
      onClose={() => setIsShareModalVisible(false)}
      title="Send Route"
      actions={[
        <Button type="button" onClick={handleShareOrder}>
          Send
        </Button>,
        <Button
          type="button"
          plain
          onClick={() => setIsShareModalVisible(false)}
        >
          Cancel
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full px-3 pb-4">
        <InfoBox message="Sending this route to another workspace will allow the route to be completed by the chosen workspace." />
        <SelectInput
          options={
            workspaceConnections?.map((connection) => ({
              label: connection.connectedWorkspace.name,
              value: connection.connectedWorkspace.id,
            })) ?? []
          }
          onChange={(value) => setSelectedWorkspaceId(value)}
          placeholder="Select a workspace"
          selected={selectedWorkspaceId}
        />
      </div>
    </Modal>
  );
}
